// src/pages/Dashboard.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import PromotionFooterWidget from "../components/Promotions/PromotionFooterWidget";
import TermsGate from "../components/TermsGate";

import {
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Space,
  Tag,
  Breadcrumb,
  Button,
  Segmented,
  Table,
  Alert,
  Skeleton,
  Empty,
} from "antd";
import {
  ReloadOutlined,
  TeamOutlined,
  DollarCircleOutlined,
  CalendarOutlined,
  PayCircleOutlined,
} from "@ant-design/icons";
import "chart.js/auto";
import { Line as LineChart, Doughnut } from "react-chartjs-2";
import "./Dashboard.css";

const { Text } = Typography;

/* ───────── helpers ───────── */
const cssVar = (name, fallback) => {
  try {
    return (
      getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim() || fallback
    );
  } catch {
    return fallback;
  }
};

const toRgba = (color, alpha = 1) => {
  if (!color) return `rgba(0,0,0,${alpha})`;
  const c = String(color).replace(/\s+/g, "");
  if (c.startsWith("rgb")) {
    const [r, g, b] = c.match(/[\d.]+/g)?.map(Number) || [0, 0, 0];
    return `rgba(${r},${g},${b},${alpha})`;
  }
  if (c[0] === "#") {
    const hex = c.slice(1);
    const full =
      hex.length === 3 ? hex.split("").map((x) => x + x).join("") : hex;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  return `rgba(0,0,0,${alpha})`;
};

const toTitle = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
const getFirstName = (a) => {
  if (!a) return "Guest";
  if (a.firstName) return toTitle(String(a.firstName).trim());
  const candidate = a.displayName || a.fullName || a.userName || a.email || "";
  if (!candidate) return "Guest";
  const raw = candidate.replace(/@.*$/, "").replace(/[._-]+/g, " ").trim();
  const first = (raw.split(/\s+/)[0] || "Guest").trim();
  return toTitle(first);
};

const resolveTimeZone = (authData) =>
  authData?.timeZone ||
  authData?.hotelTimeZone ||
  Intl.DateTimeFormat().resolvedOptions().timeZone ||
  "Africa/Gaborone";

const hourInTz = (tz) =>
  Number(
    new Intl.DateTimeFormat("en-GB", {
      hour: "numeric",
      hour12: false,
      timeZone: tz,
    }).format(new Date())
  );

const pickGreeting = (h) =>
  h >= 5 && h < 12 ? "Good morning" : h < 17 ? "Good day" : "Good evening";

const hideLegacyWelcomeSafely = () => {
  if (typeof document === "undefined") return;
  try {
    const el = document.querySelector(".legacy-welcome-banner");
    if (el) el.style.display = "none";
  } catch {}
};

/* ---- robust date extraction ---- */
const toDateOrNull = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  const s = String(v).replace(" ", "T");
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

// FIX: Prefer CheckInDate for "today" logic
const whenFromRow = (r) => {
  let v =
    r.checkIn ?? r.checkInDate ?? r.CheckIn ?? r.CheckInDate ??
    r.bookingDate ?? r.BookingDate ??
    r.createdAt ?? r.CreatedAt;

  if (!v) {
    const bn = r.bookingNo ?? r.BookingNo;
    const m = bn && String(bn).match(/^(\d{4})(\d{2})(\d{2})/);
    if (m) return new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00`);
  }
  return toDateOrNull(v);
};

/* TZ helpers */
const ymdInTz = (date, tz) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

const num = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const str = (v) => (v == null ? "" : String(v));

const pick = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return undefined;
};

/* chart gradient helpers */
const hexToRgb = (hex) => {
  const h = String(hex || "").replace("#", "");
  const f = h.length === 3 ? h.split("").map((x) => x + x).join("") : h;
  return {
    r: parseInt(f.slice(0, 2), 16) || 0,
    g: parseInt(f.slice(2, 4), 16) || 0,
    b: parseInt(f.slice(4, 6), 16) || 0,
  };
};

const makeGradient = (ctx, hex, topAlpha = 0.22, bottomAlpha = 0.02) => {
  const { r, g, b } = hexToRgb(hex);
  const canvasH = ctx?.canvas?.height || 300;
  const grad = ctx.createLinearGradient(0, 0, 0, canvasH);
  grad.addColorStop(0, `rgba(${r},${g},${b},${topAlpha})`);
  grad.addColorStop(1.0, `rgba(${r},${g},${b},${bottomAlpha})`);
  return grad;
};

const demoSeries = (n = 30) => {
  const start = new Date();
  start.setDate(start.getDate() - (n - 1));
  const out = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const base = 25 + 20 * Math.sin((i / 6) * Math.PI);
    const confirmed = Math.round(base + 10 * Math.random());
    const pending = Math.max(
      5,
      Math.round(confirmed * 0.55 + 8 * Math.sin(i / 3))
    );
    out.push({ date: d.toISOString().slice(5, 10), confirmed, pending });
  }
  return out;
};

const normalizePaymentStatus = (row) => {
  const total = num(pick(row.totalAmount, row.TotalAmount, row.amountTotal), 0);
  const paid = num(pick(row.paidAmount, row.PaidAmount, row.amountPaid), 0);

  const raw = pick(row.paymentStatus, row.PaymentStatus);
  if (raw !== undefined && raw !== null && raw !== "") {
    if (typeof raw === "number") {
      if (raw >= 2) return "Paid";
      if (raw === 1) return "PartiallyPaid";
      return "Pending";
    }
    const s = String(raw).toLowerCase().replace(/\s|-/g, "");
    if (s.includes("partial")) return "PartiallyPaid";
    if (s === "paid" || s.includes("full")) return "Paid";
    if (s.includes("pending") || s.includes("unpaid")) return "Pending";
  }

  if (total > 0) {
    if (paid >= total) return "Paid";
    if (paid > 0) return "PartiallyPaid";
    return "Pending";
  }
  return "Unknown";
};

export default function Dashboard() {
  const { authData, logout } = useAuth();
  const navigate = useNavigate();

  const hasHotelContext = !!authData?.hotelId;
  const isWeekend = useMemo(() => {
  const d = new Date().getDay(); // Sun=0 ... Fri=5 Sat=6
  return d === 5 || d === 6 || d === 0; // Fri/Sat/Sun
}, []);
  const [dashboardData, setDashboardData] = useState(null);
  const [range, setRange] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState(null);

  const [theme, setTheme] = useState(() => ({
    accent: cssVar("--accent", "#b89b6e"),
    muted: cssVar("--muted", "#87a6ff"),
    ink: cssVar("--ink", "#111"),
    ivory: cssVar("--section-ivory", "#f5f1e8"),
  }));

  useEffect(() => {
    if (typeof document === "undefined") return;
    const read = () =>
      setTheme({
        accent: cssVar("--accent", "#b89b6e"),
        muted: cssVar("--muted", "#87a6ff"),
        ink: cssVar("--ink", "#111"),
        ivory: cssVar("--section-ivory", "#f5f1e8"),
      });

    read();

    const obs = new MutationObserver(read);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    window.addEventListener("resize", read);
    return () => {
      obs.disconnect();
      window.removeEventListener("resize", read);
    };
  }, []);

  const prefersReduced =
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;

  const firstName = useMemo(() => getFirstName(authData), [authData]);
  const [greet, setGreet] = useState("Welcome");

  useEffect(() => {
    const tz0 = resolveTimeZone(authData);
    const update = () => setGreet(pickGreeting(hourInTz(tz0)));
    update();
    const id = setInterval(update, 60_000);
    const onVis = () => document.visibilityState === "visible" && update();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [authData?.timeZone, authData?.hotelTimeZone]);

  const userLocale = authData?.locale || navigator.language || "en-US";
  const currency = useCallback(
    (n) =>
      new Intl.NumberFormat(userLocale, {
        style: "currency",
        currency: dashboardData?.currency || "BWP",
        currencyDisplay: "symbol",
        maximumFractionDigits: 2,
      }).format(Number(n ?? 0)),
    [dashboardData?.currency, userLocale]
  );

  const fetchDashboardData = useCallback(async () => {
    const hotelId = authData?.hotelId;
    if (!hotelId) {
      setError("Hotel ID is not available. Please log in again.");
      setLoading(false);
      try {
        logout?.();
      } finally {
        navigate("/login", { replace: true });
      }
      return;
    }

    try {
      const res = await axiosInstance.get(`/Dashboard/${hotelId}`, {
        params: { range },
      });

      // Optional debug:
      // //console.log("Dashboard API:", res.data);

      setDashboardData(res.data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Failed to load dashboard data.");
      setDashboardData(null);
    } finally {
      setLoading(false);
      setReloading(false);
    }
  }, [authData?.hotelId, logout, navigate, range]);

  useEffect(() => {
    setLoading(true);
    fetchDashboardData();
  }, [fetchDashboardData]);

  const onRefresh = () => {
    setReloading(true);
    fetchDashboardData();
  };

  const paymentTag = (status) => {
    const map = {
      Paid: { color: "success", text: "Paid" },
      PartiallyPaid: { color: "processing", text: "Partially Paid" },
      Pending: { color: "warning", text: "Pending" },
      Unknown: { color: "default", text: "Unknown" },
    };
    const v = map[status] || map.Unknown;
    return <Tag color={v.color}>{v.text}</Tag>;
  };

  const rangeLabel = useMemo(
    () =>
      range === "30d" ? "last 30 days" : range === "90d" ? "last 90 days" : "last 7 days",
    [range]
  );

  const normalizedBookings = useMemo(() => {
    const list = Array.isArray(dashboardData?.bookingList)
      ? dashboardData.bookingList
      : [];

    return list.map((r, idx) => {
      const name =
        str(pick(r.name, r.Name)) ||
        `${str(pick(r.firstName, r.FirstName))} ${str(pick(r.lastName, r.LastName))}`.trim() ||
        "—";

      const roomType =
        str(pick(r.roomType, r.RoomType, r.roomTypeName, r.RoomTypeName)) || "—";

      const checkIn = pick(r.checkIn, r.checkInDate, r.CheckIn, r.CheckInDate);
      const checkOut = pick(r.checkOut, r.checkOutDate, r.CheckOut, r.CheckOutDate);

      const totalAmount = num(pick(r.totalAmount, r.TotalAmount, r.amountTotal), 0);
      const paidAmount = num(pick(r.paidAmount, r.PaidAmount, r.amountPaid), 0);
      const dueAmount = Math.max(totalAmount - paidAmount, 0);

      const paymentStatus = normalizePaymentStatus({ ...r, totalAmount, paidAmount });

      const when = whenFromRow(r) || toDateOrNull(checkIn) || null;

      return {
        key: r.id ?? r.Id ?? r.bookingId ?? r.BookingId ?? idx,
        raw: r,
        name,
        roomType,
        checkIn,
        checkOut,
        totalAmount,
        paidAmount,
        dueAmount,
        paymentStatus,
        when,
        hotelId: num(pick(r.hotelId, r.HotelId), 0),
      };
    });
  }, [dashboardData]);

  const tz = useMemo(() => resolveTimeZone(authData), [authData]);
  const todayKey = useMemo(() => ymdInTz(new Date(), tz), [tz]);

  // FIX: "today rows" computed by CheckIn date (not booking created date)
  const todayRows = useMemo(() => {
    return normalizedBookings.filter((r) => {
      const checkInDate = toDateOrNull(r.checkIn);
      if (!checkInDate) return false;
      return ymdInTz(checkInDate, tz) === todayKey;
    });
  }, [normalizedBookings, tz, todayKey]);

  // Prefer server "paymentsToday" (more accurate)
  const paymentsToday = useMemo(() => {
    const p = pick(dashboardData?.paymentsToday, dashboardData?.PaymentsToday);
    if (!p) return null;
    return {
      Paid: num(pick(p.paid, p.Paid), 0),
      PartiallyPaid: num(pick(p.partiallyPaid, p.PartiallyPaid), 0),
      Pending: num(pick(p.pending, p.Pending), 0),
      Other: num(pick(p.other, p.Other), 0),
      Total: num(pick(p.total, p.Total), 0),
    };
  }, [dashboardData]);

  const byStatus = useMemo(() => {
    if (paymentsToday) {
      return {
        Paid: paymentsToday.Paid,
        PartiallyPaid: paymentsToday.PartiallyPaid,
        Pending: paymentsToday.Pending,
        Other: paymentsToday.Other,
      };
    }
    // fallback if server didn’t send paymentsToday
    const agg = { Paid: 0, PartiallyPaid: 0, Pending: 0, Other: 0 };
    for (const r of todayRows) {
      const s = r.paymentStatus;
      if (s === "Paid") agg.Paid += 1;
      else if (s === "PartiallyPaid") agg.PartiallyPaid += 1;
      else if (s === "Pending") agg.Pending += 1;
      else agg.Other += 1;
    }
    return agg;
  }, [paymentsToday, todayRows]);

  const donut = useMemo(() => {
    const paid = theme.accent || "#b89b6e";
    const partial = "#8b5cf6";
    const pending = "#f59e0b";
    const other = "#d1d5db";
    const inkColor = theme.ink || "#111";
    const totalToday = paymentsToday?.Total ?? todayRows.length;

    return {
      data: {
        labels: ["Paid", "Partially Paid", "Pending", "Other"],
        datasets: [
          {
            data: [byStatus.Paid, byStatus.PartiallyPaid, byStatus.Pending, byStatus.Other],
            backgroundColor: [paid, partial, pending, other],
            borderWidth: 0,
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        cutout: "68%",
        plugins: {
          legend: { position: "bottom", labels: { color: inkColor, usePointStyle: true } },
          tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${ctx.raw}` } },
        },
      },
      totalToday,
    };
  }, [byStatus, paymentsToday, todayRows.length, theme.accent, theme.ink]);

  const graph = useMemo(() => {
    const raw0 =
      dashboardData?.reservationsGraphData?.length
        ? dashboardData.reservationsGraphData
        : demoSeries(30);

    const raw = raw0.map((d) => ({
      date: d.date ?? d.Date ?? d.label ?? d.day ?? "—",
      confirmed: num(d.confirmed ?? d.Confirmed ?? d.bookingsConfirmed ?? 0, 0),
      pending: num(d.pending ?? d.Pending ?? d.bookingsPending ?? 0, 0),
    }));

    const labels = raw.map((d) => d.date);

    const accentHex = theme.accent || "#b89b6e";
    const dashedHex = theme.muted || "#87a6ff";
    const inkToken = theme.ink || "#111";
    const gridToken = "rgba(0,0,0,0.06)";

    const safeAccentFill = (context) => {
      const ctx = context?.chart?.ctx;
      if (!ctx) return toRgba(accentHex, 0.18);
      return makeGradient(ctx, accentHex, 0.22, 0.02);
    };

    const safeMutedFill = (context) => {
      const ctx = context?.chart?.ctx;
      if (!ctx) return "rgba(0,0,0,0)";
      return makeGradient(ctx, dashedHex, 0.08, 0.0);
    };

    return {
      data: {
        labels,
        datasets: [
          {
            label: "Bookings Confirmed",
            data: raw.map((d) => d.confirmed),
            borderColor: accentHex,
            backgroundColor: safeAccentFill,
            borderWidth: 2,
            fill: true,
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 3,
          },
          {
            label: "Bookings Pending",
            data: raw.map((d) => d.pending),
            borderColor: dashedHex,
            backgroundColor: safeMutedFill,
            borderWidth: 2,
            borderDash: [6, 4],
            fill: false,
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: prefersReduced ? false : { duration: 450 },
        plugins: {
          legend: { display: true, labels: { color: inkToken, usePointStyle: true } },
          tooltip: { mode: "index", intersect: false },
        },
        interaction: { mode: "nearest", axis: "x", intersect: false },
        scales: {
          x: { ticks: { color: inkToken, maxRotation: 0, autoSkip: true }, grid: { display: false } },
          y: { beginAtZero: true, ticks: { color: inkToken }, grid: { color: gridToken, borderDash: [2, 2] } },
        },
      },
    };
  }, [dashboardData, prefersReduced, theme.accent, theme.muted, theme.ink]);

  const tableCols = useMemo(
    () => [
      { title: "Name", dataIndex: "name", key: "name", sorter: (a, b) => a.name.localeCompare(b.name) },
      { title: "Room Type", dataIndex: "roomType", key: "roomType", responsive: ["md"] },
      {
        title: "Check In",
        dataIndex: "checkIn",
        key: "checkIn",
        sorter: (a, b) => new Date(a.checkIn) - new Date(b.checkIn),
        render: (v) => (v ? new Date(String(v).replace(" ", "T")).toLocaleDateString() : "—"),
      },
      {
        title: "Check Out",
        dataIndex: "checkOut",
        key: "checkOut",
        sorter: (a, b) => new Date(a.checkOut) - new Date(b.checkOut),
        render: (v) => (v ? new Date(String(v).replace(" ", "T")).toLocaleDateString() : "—"),
      },
      {
        title: "Paid",
        dataIndex: "paidAmount",
        key: "paidAmount",
        align: "right",
        render: (v) => <Text strong>{currency(v)}</Text>,
      },
      {
        title: "Due",
        dataIndex: "dueAmount",
        key: "dueAmount",
        align: "right",
        render: (v) => <Text type={v > 0 ? "danger" : "secondary"}>{currency(v)}</Text>,
      },
      { title: "Status", dataIndex: "paymentStatus", key: "paymentStatus", render: (v) => paymentTag(v) },
    ],
    [currency]
  );

  const { todayBooking = 0, totalAmount = 0, totalRevenue = 0, totalCustomer = 0 } =
    dashboardData || {};

  const hasContent = useMemo(() => {
    const d = dashboardData || {};
    const graphLen = d?.reservationsGraphData?.length ?? 0;
    const listLen = normalizedBookings.length ?? 0;
    const anyKpi =
      (d.todayBooking ?? 0) ||
      (d.totalAmount ?? 0) ||
      (d.totalRevenue ?? 0) ||
      (d.totalCustomer ?? 0);
    return Boolean(graphLen || listLen || anyKpi);
  }, [dashboardData, normalizedBookings.length]);

  useEffect(() => {
    if (hasContent) {
      hideLegacyWelcomeSafely();
      const t1 = setTimeout(hideLegacyWelcomeSafely, 150);
      const t2 = setTimeout(hideLegacyWelcomeSafely, 600);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [hasContent]);

  if (loading) {
    return (
      <div className="lux-dash">
        <div className="dash-hero skeleton" />
        <div className="dash-body">
          <Skeleton active title paragraph={{ rows: 2 }} />
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            {[1, 2, 3, 4].map((k) => (
              <Col xs={24} sm={12} md={12} lg={6} key={k}>
                <Card className="kpi-card">
                  <Skeleton active paragraph={false} />
                </Card>
              </Col>
            ))}
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={16}>
              <Card className="chart-card" style={{ height: 320 }}>
                <Skeleton active />
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card style={{ height: 320 }}>
                <Skeleton active />
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  return (
    <div className={`lux-dash ${hasContent ? "has-content" : "is-empty"}`} style={{ background: theme.ivory }}>
      <section className="dash-hero light" role="region" aria-label="Overview">
        <div className="hero-inner">
          <div className="hero-copy">
            <div className="eyebrow">Operations Overview</div>
            <h1 className="hero-title">
              {greet}, <span className="thin">{firstName}</span>
            </h1>
            <p className="hero-sub">Quiet efficiency. Everything Matters.</p>
          </div>
          <div className="hero-controls">
            <Segmented
              size="large"
              value={range}
              onChange={setRange}
              options={[
                { label: "7 days", value: "7d" },
                { label: "30 days", value: "30d" },
                { label: "90 days", value: "90d" },
              ]}
            />
            <Button className="ghost" icon={<ReloadOutlined />} onClick={onRefresh} loading={reloading}>
              Refresh
            </Button>
          </div>
        </div>
        <div className="hero-breadcrumbs">
          <Breadcrumb>
            <Breadcrumb.Item>Home</Breadcrumb.Item>
            <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </section>

      <div className="dash-body">
        {error && (
          <Alert
            type="error"
            showIcon
            message="Could not load dashboard"
            description={error}
            style={{ marginBottom: 16 }}
          />
        )}

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={12} lg={6}>
            <Card className="kpi-card" bordered={false}>
              <Space align="center" className="kpi-line">
                <div className="kpi-icon"><CalendarOutlined /></div>
                <Statistic title="Today's Check-ins" value={todayBooking} />
              </Space>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={12} lg={6}>
            <Card className="kpi-card" bordered={false}>
              <Space align="center" className="kpi-line">
                <div className="kpi-icon"><PayCircleOutlined /></div>
                <Statistic title="Total Amount" value={totalAmount} formatter={(v) => currency(Number(v))} />
              </Space>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={12} lg={6}>
            <Card className="kpi-card" bordered={false}>
              <Space align="center" className="kpi-line">
                <div className="kpi-icon"><TeamOutlined /></div>
                <Statistic title="Total Customers" value={totalCustomer} />
              </Space>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={12} lg={6}>
            <Card className="kpi-card is-accent" bordered={false}>
              <Space align="center" className="kpi-line">
                <div className="kpi-icon"><DollarCircleOutlined /></div>
                <Statistic title="Total Revenue" value={totalRevenue} formatter={(v) => currency(Number(v))} />
              </Space>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} className="chart-row" style={{ marginTop: 16 }}>
          <Col xs={24} lg={16}>
            <Card bordered={false} className="chart-card">
              <div className="card-head">
                <div>
                  <div className="card-eyebrow">Reservations</div>
                  <div className="card-title">Trend — {rangeLabel}</div>
                </div>
              </div>
              <div className="chart-wrap">
                <LineChart data={graph.data} options={graph.options} />
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card bordered={false} className="donut-card">
              <div className="card-head">
                <div>
                  <div className="card-eyebrow">Payments</div>
                  <div className="card-title">Today — Status Mix</div>
                </div>
                <div style={{ opacity: 0.75, fontSize: 12 }}>
                  {todayKey} ({tz})
                </div>
              </div>

              <div className="donut-wrap">
                <div className="donut-canvas">
                  <Doughnut data={donut.data} options={donut.options} />
                </div>
                <div className="donut-center">
                  <div className="dc-top">{donut.totalToday}</div>
                  <div className="dc-sub">bookings</div>
                </div>
              </div>

              <div className="donut-legend">
                <span className="legend-chip paid">Paid {byStatus.Paid}</span>
                <span className="legend-chip partial">Partially {byStatus.PartiallyPaid}</span>
                <span className="legend-chip pending">Pending {byStatus.Pending}</span>
                <span className="legend-chip other">Other {byStatus.Other}</span>
              </div>

              {!donut.totalToday && (
                <div style={{ marginTop: 12, opacity: 0.85 }}>
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No check-ins recorded for today." />
                </div>
              )}
            </Card>
          </Col>
        </Row>

        <Card bordered={false} className="table-card" style={{ marginTop: 16 }}>
          <div className="card-head">
            <div>
              <div className="card-eyebrow">Bookings</div>
              <div className="card-title">Today’s Check-in List</div>
            </div>
          </div>

          <Table
            size="middle"
            columns={tableCols}
            dataSource={todayRows}
            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: [10, 20, 30] }}
            locale={{ emptyText: "No check-ins available for today." }}
            sticky
          />
        </Card>
<TermsGate />

        <PromotionFooterWidget
  visible={hasHotelContext}
  displayName={firstName}
  isWeekend={isWeekend}
  onCreatePromotion={() => {
    navigate("/hotels/promotionspage?create=1&source=dashboard-footer-widget");
  }}
/>


      </div>
    </div>
  );
}
